import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError } from '../../api/client'
import {
  assignWorkflow,
  assigningWorkflowsQuery,
  changeTicketTakeover,
  type AssignedWorkflowRow,
} from '../../api/settingsEaseedeskAssigningWorkflows'
import { LoadFailed } from '../../ui/LoadFailed'
import { SelectPicker } from '../../ui/SelectPicker'
import { toast } from '../../ui/notify'
import { Spinner } from '../../ui/Spinner'

/**
 * Settings -> Easeedesk -> Workflows -> Assigning workflows, element cu
 * element fata de `easeedesk_settings/workflows/assigning_workflows/index.php`:
 * un rand pe tip activ, cu ecusonul, butonul de preluare (comutat -> se
 * salveaza pe loc, "Settings changed") si selectorul de flux (schimbat -> se
 * salveaza pe loc, "Updates saved."), ca in `index-1.js`.
 */
export function AssigningWorkflowsPage() {
  const client = useQueryClient()
  const page = useQuery(assigningWorkflowsQuery())

  if (page.error instanceof ApiError && page.error.status === 403) {
    return (
      <div className="alert alert-danger">
        <i className="fas fa-exclamation-circle" aria-hidden="true"></i> You do not have access to this action.
      </div>
    )
  }
  if (page.error) {
    return <LoadFailed error={page.error} onRetry={() => void page.refetch()} what="This screen" />
  }
  if (!page.data) return <Spinner />

  const reload = () => client.invalidateQueries({ queryKey: ['settings', 'easeedesk', 'assigning_workflows'] })

  const onTakeover = (row: AssignedWorkflowRow, on: boolean) => {
    void changeTicketTakeover(row.ID, on)
      .then(() => {
        toast('Settings changed', 'Success')
        void reload()
      })
      .catch(() => toast('Something went wrong.', 'Error', 'error'))
  }

  const onWorkflow = (row: AssignedWorkflowRow, workflowId: string) => {
    void assignWorkflow(row.ID, workflowId)
      .then(() => {
        toast('Updates saved.', 'Success')
        void reload()
      })
      .catch(() => toast('Something went wrong.', 'Error', 'error'))
  }

  const options = [
    { value: '0', label: 'Select workflow...' },
    ...page.data.workflows.map((w) => ({ value: String(w.ID), label: w.workflow_name })),
  ]

  return (
    <div>
      <h4>
        <strong>Assigning</strong> workflows
      </h4>
      <label className="color" style={{ marginBottom: 0 }}>
        Every ticket type must have assigned a <strong>workflow</strong>
      </label>
      <hr />

      <table className="table table-striped table-hover workflow-assigning">
        <thead>
          <tr>
            <th style={{ width: 60 }}></th>
            <th className="a-left">Ticket type</th>
            <th className="a-left">Take over button</th>
            <th style={{ width: 500 }}>Assigned workflow</th>
          </tr>
        </thead>
        <tbody>
          {page.data.assigned_workflows.map((row) => (
            <tr key={row.ID}>
              <td>
                <span className="badge pull-left" style={{ minWidth: 23, backgroundColor: row.color_identifier, color: '#fff' }}>
                  {row.short_name}
                </span>
              </td>
              <td>{row.type_name}</td>
              <td>
                <form className="change_takeover_button" style={{ marginLeft: 20 }} onSubmit={(e) => e.preventDefault()}>
                  <input
                    type="checkbox"
                    name="take_over"
                    checked={row.takeover_button === 1}
                    onChange={(e) => onTakeover(row, e.target.checked)}
                  />
                  <input type="hidden" name="ticket_type" value={row.ID} />
                </form>
              </td>
              <td>
                <form className="assign-workflow" onSubmit={(e) => e.preventDefault()}>
                  <input type="hidden" name="ticket_type" value={row.ID} />
                  <SelectPicker
                    id={`assign-workflow-${row.ID}`}
                    name="workflow_id"
                    className="form-control input-sm select-assign-workflow"
                    title="Select workflow..."
                    options={options}
                    value={String(row.workflow_id)}
                    onChange={(value) => onWorkflow(row, value)}
                  />
                </form>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
